import {
  BhsStatusBucket,
  IResource,
  IResourceDetail,
} from "../../components-v2/overview/overview-cards/resources-availability/ResourceAvailability.types";
import { ResourceDisplayTitle } from "../../enum/ResourceAvailabilityEnum";
import {
  IApiResourceItem,
  IApiResourceResponse,
  IBaggageItem,
} from "../../models/resource-allocation/IResourceAvailabilityCard";

const RESOURCE_TITLE_MAP: Record<string, string> = {
  stands: ResourceDisplayTitle.stands,
  gates: ResourceDisplayTitle.gates,
};

const RESOURCE_TYPES = ["stands", "gates"] as const;

type ResourceStatus = "inUse" | "available" | "nonOperational" | "unknown";

function normalizeText(s?: string) {
  return (s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function isStatusOperational(status?: string) {
  const s = normalizeText(status);
  return (
    s === "OP" ||
    s === "OPERATIONAL" ||
    s === "AVAILABLE" ||
    s === "INSERVICE" ||
    s === "IN_SERVICE"
  );
}

function isStatusNonOperational(status?: string) {
  const s = normalizeText(status);
  return (
    s === "NONOPERATIONAL" ||
    s === "NOTOPERATIONAL" ||
    s.includes("MAINT") ||
    s.includes("OUTOFSERVICE") ||
    s.includes("FAILED") ||
    s === "NOTOP"
  );
}

function classifyItem(item: IApiResourceItem): ResourceStatus {
  if (isStatusNonOperational(item.status)) {
    return "nonOperational";
  }

  if (isStatusOperational(item.status)) {
    const occ = normalizeText(item.occupancy);
    if (occ === "UNOCCUPIED" || occ === "AVAILABLE") {
      return "available";
    }
    if (occ === "OCCUPIED" || occ === "INUSE" || occ === "IN_USE") {
      return "inUse";
    }
    return "unknown";
  }

  return "unknown";
}

function processBaggageItems(baggageItems: IBaggageItem[]): IResource[] {
  return baggageItems.map((baggage) => {
    const scanners = baggage.associatedResources?.scanners ?? [];
    const bhsDetail: IResourceDetail = { name: baggage.name, terminal: "" };
    const bhsIsOp = isStatusOperational(baggage.status);
    const bhsIsNonOp = isStatusNonOperational(baggage.status);

    const scannerAvailable: IResourceDetail[] = scanners
      .filter((s) => isStatusOperational(s.status))
      .map((s) => ({ name: s.subCode, terminal: "" }));
    const scannerNonOperational: IResourceDetail[] = scanners
      .filter((s) => isStatusNonOperational(s.status))
      .map((s) => ({ name: s.subCode, terminal: "" }));
    const scannerUnknown: IResourceDetail[] = scanners
      .filter(
        (s) =>
          !isStatusOperational(s.status) && !isStatusNonOperational(s.status)
      )
      .map((s) => ({ name: s.subCode, terminal: "" }));

    // Determine which bucket the BHS belongs to
    let bhsStatusBucket: BhsStatusBucket;
    if (bhsIsOp) {
      bhsStatusBucket = "available";
    } else if (bhsIsNonOp) {
      bhsStatusBucket = "nonOperational";
    } else {
      bhsStatusBucket = "unknown";
    }

    const available: IResourceDetail[] = bhsIsOp
      ? [bhsDetail, ...scannerAvailable]
      : scannerAvailable;
    const nonOperational: IResourceDetail[] = bhsIsNonOp
      ? [bhsDetail, ...scannerNonOperational]
      : scannerNonOperational;
    const unknown: IResourceDetail[] =
      !bhsIsOp && !bhsIsNonOp ? [bhsDetail, ...scannerUnknown] : scannerUnknown;

    return {
      title: baggage.name,
      total: 1 + scanners.length,
      inUse: 0,
      available: available.length,
      nonOperational: nonOperational.length,
      unknown: unknown.length,
      overallStatus: baggage.overallStatus,
      tooltipHeader: bhsDetail,
      bhsStatusBucket,
      details: {
        inUse: [],
        available: scannerAvailable,
        nonOperational: scannerNonOperational,
        unknown: scannerUnknown,
      },
    };
  });
}

export function transformApiResponse(
  response: IApiResourceResponse
): IResource[] {
  if (!response?.data) {
    return [];
  }

  const result: IResource[] = [];

  // Standard resources: plural stands/gates
  for (const type of RESOURCE_TYPES) {
    const items = response.data[type];
    if (!items || items.length === 0) {
      continue;
    }

    const details: Record<string, IResourceDetail[]> = {
      inUse: [],
      available: [],
      nonOperational: [],
      unknown: [],
    };
    for (const item of items) {
      const bucket = classifyItem(item);
      details[bucket].push({ name: item.name, terminal: "" });
    }

    result.push({
      title: RESOURCE_TITLE_MAP[type],
      total: items.length,
      inUse: details.inUse.length,
      available: details.available.length,
      nonOperational: details.nonOperational.length,
      unknown: details.unknown.length,
      details: {
        inUse: details.inUse,
        available: details.available,
        nonOperational: details.nonOperational,
        unknown: details.unknown,
      },
    });
  }

  // Baggage: each baggage item becomes its own row, counts/tooltips driven by its scanners
  // Support both singular "baggage" (new API) and plural "baggages" (legacy)
  const baggageItems = response.data.baggage ?? response.data.baggages;
  if (baggageItems && baggageItems.length > 0) {
    result.push(...processBaggageItems(baggageItems));
  }

  return result.filter((r) => r.total > 0);
}
